import React from 'react';
import { TableCell, TableRow } from '@material-ui/core';

export const groupInvoiceLines = (items, getEntityGroupName) => {
  const groups = [];
  const byName = new Map();

  (items || []).forEach((item) => {
    const resolvedName = String(getEntityGroupName(item) || '').trim();
    const name = resolvedName || 'Ungrouped';
    if (!byName.has(name)) {
      const group = { name, items: [] };
      byName.set(name, group);
      groups.push(group);
    }
    byName.get(name).items.push(item);
  });

  return groups;
};

const numberValue = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => `$${numberValue(value).toLocaleString()}`;

const accountingMoney = (value) =>
  `$${numberValue(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const hasCommissionValue = (value) =>
  value != null && String(value).trim() !== '';

const normalizeCommissionType = (commission) => {
  const raw = String(
    commission?.commissionType ?? commission?.Type ?? commission?.type ?? ''
  )
    .trim()
    .toLowerCase();
  if (raw === 'percentage' || raw === 'percent' || raw === 'profit') {
    return 'percentage';
  }
  if (raw === 'rate') return 'rate';
  if (raw === 'amount' || raw === 'individual' || raw === 'fixed') {
    return 'individual';
  }
  if (
    hasCommissionValue(commission?.percent) ||
    hasCommissionValue(commission?.Percentage) ||
    hasCommissionValue(commission?.percentage)
  ) {
    return 'percentage';
  }
  if (
    hasCommissionValue(commission?.rate) ||
    hasCommissionValue(commission?.Rate)
  ) {
    return 'rate';
  }
  return 'individual';
};

const commissionAmount = (commission) =>
  numberValue(commission?.commissionAmount ?? commission?.CommissionAmount ?? 0);

const commissionPercent = (commission) =>
  numberValue(
    commission?.percent ?? commission?.Percentage ?? commission?.percentage ?? 0
  );

export const buildInvoiceAccounting = ({
  items,
  commissions,
  getQuantity,
  getUnitPrice,
  getTotalPrice,
  getUnitCost,
}) => {
  const sourceItems = items || [];
  const percentageCommissions = (commissions || []).filter(
    (commission) => normalizeCommissionType(commission) === 'percentage'
  );
  const fixedCommissions = (commissions || []).filter(
    (commission) => normalizeCommissionType(commission) !== 'percentage'
  );
  const lineProfits = sourceItems.map((item) => {
    const quantity = numberValue(getQuantity(item));
    const unitPrice = numberValue(getUnitPrice(item));
    const unitCost = numberValue(getUnitCost(item));
    return Math.max(0, (unitPrice - unitCost) * quantity);
  });
  const totalProfit = lineProfits.reduce((sum, value) => sum + value, 0);
  const cpByItem = sourceItems.map(() => 0);

  percentageCommissions.forEach((commission) => {
    const percent = commissionPercent(commission);
    const calculatedTotal = totalProfit * percent / 100;
    const storedAmount = commissionAmount(commission);
    const targetTotal = storedAmount > 0 ? storedAmount : calculatedTotal;
    if (targetTotal <= 0 || totalProfit <= 0) return;

    let allocated = 0;
    const eligibleIndexes = lineProfits
      .map((profit, index) => ({ profit, index }))
      .filter(({ profit }) => profit > 0);
    eligibleIndexes.forEach(({ profit, index }, eligibleIndex) => {
      const amount =
        eligibleIndex === eligibleIndexes.length - 1
          ? Math.round((targetTotal - allocated) * 100) / 100
          : Math.round((targetTotal * profit / totalProfit) * 100) / 100;
      cpByItem[index] += amount;
      allocated += amount;
    });
  });

  const itemRows = sourceItems.map((item, index) => {
    const quantity = numberValue(getQuantity(item));
    const unitPrice = numberValue(getUnitPrice(item));
    const totalSales = numberValue(getTotalPrice(item));
    const unitCost = numberValue(getUnitCost(item));
    const cpCommission = Math.round(cpByItem[index] * 100) / 100;
    return {
      item,
      quantity,
      unitPrice,
      totalSales,
      unitCost,
      cpCommission,
      qbTotal: Math.round((totalSales - cpCommission) * 100) / 100,
    };
  });
  const fixedRows = fixedCommissions.map((commission) => ({
    commission,
    amount: commissionAmount(commission),
  }));
  const itemTotalSales = itemRows.reduce((sum, row) => sum + row.totalSales, 0);
  const fixedTotal = fixedRows.reduce((sum, row) => sum + row.amount, 0);
  const cpTotal = itemRows.reduce((sum, row) => sum + row.cpCommission, 0);

  return {
    itemRows,
    fixedRows,
    totalQuantity: itemRows.reduce((sum, row) => sum + row.quantity, 0),
    totalSales: itemTotalSales + fixedTotal,
    cpTotal,
    qbTotal: itemTotalSales - cpTotal + fixedTotal,
  };
};

const InvoiceLineRows = ({
  items,
  grouped,
  getEntityGroupName,
  getItemNumber,
  getItemName,
  getQuantity,
  getUnitPrice,
  getTotalPrice,
  getUnitCost = () => 0,
  getRowKey,
  showAccountingColumns = false,
  commissions = [],
}) => {
  const accounting = buildInvoiceAccounting({
    items,
    commissions,
    getQuantity,
    getUnitPrice,
    getTotalPrice,
    getUnitCost,
  });
  const accountingByItem = new Map(
    accounting.itemRows.map((row) => [row.item, row])
  );
  const columnCount = showAccountingColumns ? 8 : 5;

  const renderItem = (item, index, keyPrefix = 'item') => {
    const rowAccounting = accountingByItem.get(item);
    return (
    <TableRow key={`${keyPrefix}-${getRowKey(item, index)}-${index}`}>
      <TableCell>{getItemNumber(item)}</TableCell>
      <TableCell>{getItemName(item)}</TableCell>
      <TableCell align="right">{numberValue(getQuantity(item)).toLocaleString()}</TableCell>
      <TableCell align="right">
        {showAccountingColumns
          ? accountingMoney(getUnitPrice(item))
          : money(getUnitPrice(item))}
      </TableCell>
      <TableCell align="right">
        {showAccountingColumns
          ? accountingMoney(getTotalPrice(item))
          : money(getTotalPrice(item))}
      </TableCell>
      {showAccountingColumns && (
        <>
          <TableCell align="right">{accountingMoney(rowAccounting?.unitCost)}</TableCell>
          <TableCell align="right">{accountingMoney(rowAccounting?.cpCommission)}</TableCell>
          <TableCell align="right">{accountingMoney(rowAccounting?.qbTotal)}</TableCell>
        </>
      )}
    </TableRow>
    );
  };

  const renderFixedCommissionRows = () =>
    showAccountingColumns &&
    accounting.fixedRows.map(({ commission, amount }, index) => (
      <TableRow key={`invoice-fixed-commission-${index}`}>
        <TableCell />
        <TableCell>
          {commission?.commissionName || commission?.CommissionName || 'Commission'}
          {(commission?.description || commission?.Description) && (
            <div style={{ fontSize: 12, color: '#555' }}>
              {commission?.description || commission?.Description}
            </div>
          )}
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell align="right">{accountingMoney(amount)}</TableCell>
        <TableCell />
        <TableCell align="right">{accountingMoney(0)}</TableCell>
        <TableCell align="right">{accountingMoney(amount)}</TableCell>
      </TableRow>
    ));

  const renderAccountingSubtotal = () =>
    showAccountingColumns && (
      <TableRow style={{ backgroundColor: '#f5f5f5' }}>
        <TableCell colSpan={2} style={{ fontWeight: 700 }}>
          Subtotal
        </TableCell>
        <TableCell align="right" style={{ fontWeight: 700 }}>
          {accounting.totalQuantity.toLocaleString()}
        </TableCell>
        <TableCell />
        <TableCell align="right" style={{ fontWeight: 700 }}>
          {accountingMoney(accounting.totalSales)}
        </TableCell>
        <TableCell />
        <TableCell align="right" style={{ fontWeight: 700 }}>
          {accountingMoney(accounting.cpTotal)}
        </TableCell>
        <TableCell align="right" style={{ fontWeight: 700 }}>
          {accountingMoney(accounting.qbTotal)}
        </TableCell>
      </TableRow>
    );

  if (!grouped) {
    return (
      <>
        {(items || []).map((item, index) => renderItem(item, index))}
        {renderFixedCommissionRows()}
        {renderAccountingSubtotal()}
      </>
    );
  }

  return (
    <>
      {groupInvoiceLines(items, getEntityGroupName).map((group, groupIndex) => {
        const totalQuantity = group.items.reduce(
          (sum, item) => sum + numberValue(getQuantity(item)),
          0
        );
        const totalPrice = group.items.reduce(
          (sum, item) => sum + numberValue(getTotalPrice(item)),
          0
        );
        return (
          <React.Fragment key={`invoice-group-${group.name}-${groupIndex}`}>
            <TableRow style={{ backgroundColor: '#eeeeee' }}>
              <TableCell colSpan={columnCount} style={{ fontWeight: 700 }}>
                {group.name}
              </TableCell>
            </TableRow>
            {group.items.map((item, itemIndex) =>
              renderItem(item, itemIndex, `group-${groupIndex}`)
            )}
            <TableRow style={{ backgroundColor: '#fafafa' }}>
              <TableCell colSpan={2} style={{ fontWeight: 700 }}>
                {group.name} Total
              </TableCell>
              <TableCell align="right" style={{ fontWeight: 700 }}>
                {totalQuantity.toLocaleString()}
              </TableCell>
              <TableCell align="right" style={{ fontWeight: 700 }}>
                {showAccountingColumns ? 'Total Sales:' : 'Total Price:'}
              </TableCell>
              <TableCell align="right" style={{ fontWeight: 700 }}>
                {showAccountingColumns
                  ? accountingMoney(totalPrice)
                  : money(totalPrice)}
              </TableCell>
              {showAccountingColumns && (
                <>
                  <TableCell />
                  <TableCell align="right" style={{ fontWeight: 700 }}>
                    {accountingMoney(
                      group.items.reduce(
                        (sum, item) => sum + (accountingByItem.get(item)?.cpCommission || 0),
                        0
                      )
                    )}
                  </TableCell>
                  <TableCell align="right" style={{ fontWeight: 700 }}>
                    {accountingMoney(
                      group.items.reduce(
                        (sum, item) => sum + (accountingByItem.get(item)?.qbTotal || 0),
                        0
                      )
                    )}
                  </TableCell>
                </>
              )}
            </TableRow>
          </React.Fragment>
        );
      })}
      {renderFixedCommissionRows()}
      {renderAccountingSubtotal()}
    </>
  );
};

export default InvoiceLineRows;
